/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Picker } from "@spectrum-web-components/picker";
import { useEventListener } from "hooks/useEventListener";
import { useRef, CSSProperties } from "react";
import * as React from "react";

export type SharedPickerOptionValue = string;

export interface SharedPickerOption {
  name: string;
  value: SharedPickerOptionValue;
}

export interface SharedPickerProps {
  options: SharedPickerOption[];
  selectedValue: SharedPickerOptionValue;
  onOptionSelected: (selectedOptionValue: SharedPickerOptionValue) => void;
  isQuiet?: boolean;
  pickerSize?: "s" | "m" | "l" | "xl";
  pickerStyles?: CSSProperties;
}

export const SharedPicker: React.FC<SharedPickerProps> = ({
  options,
  selectedValue,
  onOptionSelected,
  isQuiet = false,
  pickerSize = "m",
  pickerStyles = { marginTop: 8 },
  // Default styles
}) => {
  const pickerRef = useRef<Picker>(null);

  useEventListener(
    "change",
    (event: Event): void => {
      const picker = event.target as Picker;
      onOptionSelected(picker.value);
    },
    pickerRef.current
  );

  const selectedOption = options.find(
    (option) => option.value === selectedValue
  );

  return isQuiet ? ( // Conditional rendering based on isQuiet prop - the presence whether true or false seems to enforce truthy value
    <sp-picker
      size={pickerSize}
      label={selectedOption?.name}
      value={selectedOption?.value}
      ref={pickerRef}
      style={pickerStyles}
      quiet={isQuiet}
    >
      {options.map((option) => (
        <sp-menu-item key={option.value} value={option.value}>
          {option.name}
        </sp-menu-item>
      ))}
    </sp-picker>
  ) : (
    <sp-picker
      size={pickerSize}
      label={selectedOption?.name}
      value={selectedOption?.value}
      ref={pickerRef}
      style={pickerStyles}
    >
      {options.map((option) => (
        <sp-menu-item key={option.value} value={option.value}>
          {option.name}
        </sp-menu-item>
      ))}
    </sp-picker>
  );
};
